import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { formatDateTime } from '../utils/dateUtils';
import {
  FiAlertCircle,
  FiCheckCircle,
  FiInfo,
  FiUser,
  FiXCircle,
  FiEye,
  FiX,
  FiClock,
  FiTool,
  FiHome,
  FiFileText,
  FiCheck,
  FiStar
} from 'react-icons/fi';

function CriticalFixes() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFix, setSelectedFix] = useState(null);
  const [actionNote, setActionNote] = useState('');
  const [activeSection, setActiveSection] = useState('pending');
  const [detailsFix, setDetailsFix] = useState(null);

  const getWorkDoneBy = (req) => {
    if (!req) return 'Not assigned';
    return (
      req.metadata?.flow_work_done_by ||
      req.work_done_by ||
      req.completed_by_staff_name ||
      req.metadata?.completed_by_staff_name ||
      req.metadata?.dashboard_completed_by ||
      req.metadata?.staff_acknowledged_by_name ||
      req.assigned_to_name ||
      'Not assigned'
    );
  };

  const getRemarks = (req) => {
    if (!req) return '';
    return (
      req.metadata?.flow_remarks ||
      req.remarks ||
      req.metadata?.completion_remarks ||
      req.metadata?.staff_completion_remarks ||
      ''
    );
  };

  const getDisplayId = (req) => {
    if (!req) return 'N/A';
    if (req.request_number) return `#${req.request_number}`;
    if (req.id) return `#${String(req.id).slice(0, 8)}`;
    return 'N/A';
  };

  const fetchCriticalFixes = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/roomservice/requests?all=true');
      const allList = Array.isArray(res.data) ? res.data : [];
      const criticalFixes = allList.filter((req) => {
        const status = req.metadata?.critical_fix_status;
        const dispatchStatus = req.dispatch_status || req.metadata?.dispatch_status;
        const completionResponse = String(req.completion_response || req.metadata?.completion_response || '').toLowerCase();

        const hasCriticalStatus = status && ['review_pending', 'confirmed', 'resolved', 'cancelled'].includes(status);
        const isCriticalDispatch = dispatchStatus === 'critical_escalated';
        const isCriticalResponse = completionResponse === 'critical';

        return hasCriticalStatus || isCriticalDispatch || isCriticalResponse;
      });
      setRequests(criticalFixes);
    } catch (err) {
      console.error('Failed to fetch critical fixes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCriticalFixes();
    const interval = setInterval(fetchCriticalFixes, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const handleUpdateStatus = async (id, status, note = '') => {
    try {
      await api.put(`/api/roomservice/request/${id}/critical-fix`, {
        critical_fix_status: status,
        critical_fix_note: note
      });
      await fetchCriticalFixes();
      setSelectedFix(null);
      setActionNote('');
    } catch (err) {
      console.error('Failed to update critical fix:', err);
      alert('Failed to update critical fix status');
    }
  };

  const reviewPending = useMemo(() => requests.filter((req) => {
    const status = req.metadata?.critical_fix_status;
    const dispatchStatus = req.dispatch_status || req.metadata?.dispatch_status;
    const completionResponse = String(req.completion_response || req.metadata?.completion_response || '').toLowerCase();

    if (status === 'confirmed' || status === 'resolved' || status === 'cancelled') return false;
    return status === 'review_pending' || dispatchStatus === 'critical_escalated' || completionResponse === 'critical';
  }), [requests]);

  const confirmedFixes = useMemo(
    () => requests.filter((req) => req.metadata?.critical_fix_status === 'confirmed'),
    [requests]
  );

  const resolvedFixes = useMemo(
    () => requests.filter((req) => req.metadata?.critical_fix_status === 'resolved'),
    [requests]
  );

  const cancelledFixes = useMemo(
    () => requests.filter((req) => req.metadata?.critical_fix_status === 'cancelled'),
    [requests]
  );

  const visibleRequests = useMemo(() => {
    switch (activeSection) {
      case 'pending': return reviewPending;
      case 'confirmed': return confirmedFixes;
      case 'resolved': return resolvedFixes;
      case 'cancelled': return cancelledFixes;
      default: return [];
    }
  }, [activeSection, reviewPending, confirmedFixes, resolvedFixes, cancelledFixes]);

  const sections = [
    { id: 'pending', label: 'Pending', count: reviewPending.length, activeClass: 'bg-amber-600 text-white border-amber-600', badgeClass: 'bg-amber-50 text-amber-700' },
    { id: 'confirmed', label: 'Confirmed', count: confirmedFixes.length, activeClass: 'bg-red-600 text-white border-red-600', badgeClass: 'bg-red-50 text-red-700' },
    { id: 'resolved', label: 'Resolved', count: resolvedFixes.length, activeClass: 'bg-emerald-600 text-white border-emerald-600', badgeClass: 'bg-emerald-50 text-emerald-700' },
    { id: 'cancelled', label: 'Cancelled', count: cancelledFixes.length, activeClass: 'bg-gray-800 text-white border-gray-800', badgeClass: 'bg-gray-100 text-gray-700' }
  ];

  if (loading && requests.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-3 sm:p-6 max-w-7xl mx-auto space-y-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FiAlertCircle className="text-rose-500" />
            Critical Fixes
          </h1>
          <div className="text-xs text-gray-500">Updated automatically every 30 seconds</div>
        </div>

        {/* Section Navigation Tabs */}
        <div className="flex items-center gap-2 flex-wrap">
          {sections.map((sec) => {
            const isActive = activeSection === sec.id;
            return (
              <button
                key={sec.id}
                onClick={() => setActiveSection(sec.id)}
                className={`px-3.5 py-2 rounded-lg text-sm font-semibold border transition-all flex items-center gap-2 ${
                  isActive
                    ? `${sec.activeClass} shadow-sm`
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                }`}
              >
                <span>{sec.label}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                  isActive ? 'bg-white/25 text-white' : sec.badgeClass
                }`}>
                  {sec.count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="text-sm text-gray-600">
          {visibleRequests.length} {activeSection} critical fix request{visibleRequests.length === 1 ? '' : 's'}
        </div>

        {visibleRequests.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-500">
            No {activeSection} critical fixes found.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {visibleRequests.map((req) => {
              const staffName = req.completed_by_staff_name || req.assigned_to_name || req.metadata?.completed_by_staff_name || req.metadata?.assigned_staff_name || 'Not assigned';
              const isPending = activeSection === 'pending';
              const isConfirmed = activeSection === 'confirmed';
              const isResolved = activeSection === 'resolved';
              const isCancelled = activeSection === 'cancelled';

              let timeDisplay = req.created_at;
              let timeLabel = 'Created';
              if (isConfirmed && req.metadata?.critical_fix_confirmed_at) {
                timeDisplay = req.metadata.critical_fix_confirmed_at;
                timeLabel = 'Confirmed';
              } else if (isResolved && req.metadata?.critical_fix_resolved_at) {
                timeDisplay = req.metadata.critical_fix_resolved_at;
                timeLabel = 'Resolved';
              } else if (isCancelled && req.metadata?.critical_fix_cancelled_at) {
                timeDisplay = req.metadata.critical_fix_cancelled_at;
                timeLabel = 'Cancelled';
              }

              return (
                <div key={req.id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-3 flex flex-col justify-between">
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                        Room {req.room_no || 'N/A'}
                      </span>
                      <span className="text-xs text-gray-500" title={`${timeLabel} time`}>
                        {timeLabel}: {formatDateTime(timeDisplay)}
                      </span>
                    </div>

                    <div>
                      <p className="text-sm font-semibold text-gray-900">{req.requested_activity || req.category}</p>
                      <p className="text-sm text-gray-700 mt-1 line-clamp-3">{req.details}</p>
                    </div>

                    <div className="rounded-lg bg-gray-50 border border-gray-200 p-2.5 space-y-1">
                      <p className="text-xs text-gray-600 flex items-center gap-1.5">
                        <FiUser className="h-3.5 w-3.5" />
                        Staff: <span className="font-semibold text-gray-800">{staffName}</span>
                      </p>
                      {req.metadata?.critical_fix_note && (
                        <p className="text-xs text-gray-600">
                          Note: <span className="text-gray-800">{req.metadata.critical_fix_note}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Actions / Status badges */}
                  <div className="pt-2 border-t border-gray-100 space-y-2">
                    <button
                      type="button"
                      onClick={() => setDetailsFix(req)}
                      className="w-full py-1.5 px-3 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 text-gray-700 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
                    >
                      <FiEye className="w-3.5 h-3.5 text-gray-500" />
                      <span>View Details</span>
                    </button>

                    {isPending && (
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => {
                            setActionNote('');
                            setSelectedFix({ ...req, action: 'confirm' });
                          }}
                          className="w-full py-2 px-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs font-semibold flex items-center justify-center gap-1 shadow-sm"
                        >
                          <FiAlertCircle className="h-3.5 w-3.5 shrink-0" />
                          <span>Confirm</span>
                        </button>
                        <button
                          onClick={() => {
                            setActionNote('');
                            setSelectedFix({ ...req, action: 'cancel' });
                          }}
                          className="w-full py-2 px-2 bg-gray-100 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors text-xs font-semibold flex items-center justify-center gap-1"
                        >
                          <FiXCircle className="h-3.5 w-3.5 text-gray-500 shrink-0" />
                          <span>No</span>
                        </button>
                      </div>
                    )}

                    {isConfirmed && (
                      <button
                        onClick={() => {
                          setActionNote('');
                          setSelectedFix({ ...req, action: 'resolve' });
                        }}
                        className="w-full py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors text-xs font-semibold flex items-center justify-center gap-1.5"
                      >
                        <FiCheckCircle className="h-4 w-4" />
                        Mark Resolved
                      </button>
                    )}

                    {isResolved && (
                      <div className="w-full py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5">
                        <FiCheckCircle className="h-4 w-4 text-emerald-600" />
                        Resolved Critical Fix
                      </div>
                    )}

                    {isCancelled && (
                      <div className="w-full py-2 bg-slate-50 text-slate-700 border border-slate-200 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5">
                        <FiCheckCircle className="h-4 w-4 text-slate-500" />
                        Cancelled · Completed in Service Requests
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Action / Confirmation Modal */}
      {selectedFix && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-md w-full p-5 sm:p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-2">
              {selectedFix.action === 'confirm' && 'Confirm Critical Fix'}
              {selectedFix.action === 'cancel' && 'Mark as Not a Critical Fix (No)'}
              {selectedFix.action === 'resolve' && 'Mark Critical Fix as Resolved'}
            </h2>

            <div className="mb-4 bg-gray-50 p-3 rounded-lg text-sm text-gray-700 space-y-1">
              <p><strong>Room:</strong> {selectedFix.room_no || 'N/A'}</p>
              <p><strong>Department:</strong> {selectedFix.requested_activity || selectedFix.category}</p>
              <p className="mt-1"><strong>Issue:</strong> {selectedFix.details}</p>
            </div>

            {selectedFix.action === 'cancel' && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-start gap-2">
                <FiInfo className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <span>
                  Selecting <strong>No</strong> will move this request to the <strong>Cancelled</strong> critical fixes section and update its status to <strong>Completed</strong> in Service Requests.
                </span>
              </div>
            )}

            {selectedFix.action === 'resolve' && (
              <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800 flex items-start gap-2">
                <FiInfo className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                <span>
                  This will move the request to the <strong>Resolved</strong> critical fixes section while keeping its status as <strong>Critical Fix</strong> in reports and analytics.
                </span>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {selectedFix.action === 'cancel' ? 'Add Reason / Note (optional)' : 'Add Note / Explanation (optional)'}
              </label>
              <textarea
                value={actionNote}
                onChange={(e) => setActionNote(e.target.value)}
                rows={3}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 border p-2 text-sm"
                placeholder={
                  selectedFix.action === 'cancel'
                    ? 'Reason why this is not a critical fix...'
                    : 'Briefly explain the issue, next steps, or resolution...'
                }
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setSelectedFix(null);
                  setActionNote('');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 text-sm font-semibold"
              >
                Cancel
              </button>

              {selectedFix.action === 'confirm' && (
                <button
                  onClick={() => handleUpdateStatus(selectedFix.id, 'confirmed', actionNote)}
                  className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 text-sm font-semibold"
                >
                  Confirm as Critical Fix
                </button>
              )}

              {selectedFix.action === 'cancel' && (
                <button
                  onClick={() => handleUpdateStatus(selectedFix.id, 'cancelled', actionNote)}
                  className="px-4 py-2 text-white bg-gray-900 rounded-md hover:bg-gray-800 text-sm font-semibold"
                >
                  Confirm (Mark as Completed)
                </button>
              )}

              {selectedFix.action === 'resolve' && (
                <button
                  onClick={() => handleUpdateStatus(selectedFix.id, 'resolved', actionNote)}
                  className="px-4 py-2 text-white bg-emerald-600 rounded-md hover:bg-emerald-700 text-sm font-semibold"
                >
                  Mark Resolved
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Request Details Modal */}
      {detailsFix && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl border border-gray-200 overflow-hidden flex flex-col max-h-[92vh]">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50/80">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-rose-50 border border-rose-200 flex items-center justify-center text-rose-600 shrink-0">
                  <FiAlertCircle size={20} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-bold text-gray-900">Critical Fix Details</h3>
                    <span className="text-xs font-mono font-semibold text-gray-500 bg-gray-200/80 px-2 py-0.5 rounded">
                      {getDisplayId(detailsFix)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Room {detailsFix.room_no || 'N/A'} • Created {formatDateTime(detailsFix.created_at)}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDetailsFix(null)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition cursor-pointer"
              >
                <FiX size={18} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-5 overflow-y-auto space-y-4 text-xs sm:text-sm flex-1">
              {/* Status Banner */}
              <div className="flex items-center justify-between flex-wrap gap-2 p-3 rounded-xl bg-slate-50 border border-slate-200">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-gray-500 font-medium">Critical Fix Status:</span>
                  {detailsFix.metadata?.critical_fix_status === 'confirmed' && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-800 border border-red-200">
                      <FiAlertCircle size={12} />
                      Confirmed Critical Fix
                    </span>
                  )}
                  {detailsFix.metadata?.critical_fix_status === 'resolved' && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                      <FiCheckCircle size={12} />
                      Resolved Critical Fix
                    </span>
                  )}
                  {detailsFix.metadata?.critical_fix_status === 'cancelled' && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-gray-200 text-gray-800 border border-gray-300">
                      <FiXCircle size={12} />
                      Marked Not Critical (Completed)
                    </span>
                  )}
                  {(!detailsFix.metadata?.critical_fix_status || detailsFix.metadata?.critical_fix_status === 'review_pending') && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200">
                      <FiClock size={12} />
                      Pending Manager Review
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-500">
                  Request Lifecycle: <strong className="text-gray-800 capitalize">{detailsFix.status || 'Received'}</strong>
                </span>
              </div>

              {/* Guest & Room Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-white rounded-xl border border-gray-200 p-3.5 space-y-1.5">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-gray-700">
                    <FiHome className="text-gray-500" />
                    <span>Guest &amp; Room Info</span>
                  </div>
                  <div className="space-y-1 pt-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Room Number:</span>
                      <span className="font-bold text-gray-900">Room {detailsFix.room_no || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Guest Name:</span>
                      <span className="font-semibold text-gray-800">{detailsFix.guests?.name || detailsFix.guest_name || 'Guest'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Guest Phone:</span>
                      <span className="font-mono text-gray-700">{detailsFix.guests?.phone || detailsFix.phone || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 p-3.5 space-y-1.5">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-gray-700">
                    <FiTool className="text-gray-500" />
                    <span>Service &amp; Department</span>
                  </div>
                  <div className="space-y-1 pt-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Department:</span>
                      <span className="font-semibold text-gray-900">{detailsFix.category || 'General'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Requested Activity:</span>
                      <span className="font-semibold text-gray-800">{detailsFix.requested_activity || detailsFix.category || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Dispatch Status:</span>
                      <span className="text-gray-700">{detailsFix.dispatch_status || detailsFix.metadata?.dispatch_status || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Request Details Description */}
              <div className="bg-white rounded-xl border border-gray-200 p-3.5 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
                    <FiFileText className="text-gray-500" />
                    Request Details &amp; Issue
                  </span>
                  {detailsFix.metadata?.emergency_type && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-800 border border-rose-200">
                      {String(detailsFix.metadata.emergency_type).toLowerCase() === 'medical' ? 'Medical Emergency' : 'Security Emergency'}
                    </span>
                  )}
                </div>
                <div className="p-3 bg-gray-50/80 rounded-lg border border-gray-100 text-gray-800 text-xs sm:text-sm whitespace-pre-wrap leading-relaxed">
                  {detailsFix.details || 'No details provided'}
                </div>
              </div>

              {/* Work Done & Remarks Box */}
              <div className="bg-white rounded-xl border border-gray-200 p-3.5 space-y-2">
                <span className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
                  <FiCheck className="text-emerald-600" />
                  Work Done &amp; Staff Completion Remarks
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                  <div className="space-y-1">
                    <span className="text-gray-500 text-xs">Work Done By:</span>
                    <p className="font-bold text-gray-900 bg-gray-50 px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs">
                      {getWorkDoneBy(detailsFix)}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <span className="text-gray-500 text-xs">Completion Outcome Check:</span>
                    <p className="font-semibold text-rose-700 bg-rose-50 px-2.5 py-1.5 rounded-lg border border-rose-200 text-xs">
                      {detailsFix.completion_response || detailsFix.metadata?.completion_response || 'No, Critical Fix'}
                    </p>
                  </div>
                </div>

                <div className="space-y-1 pt-1">
                  <span className="text-gray-500 text-xs">Staff Remarks:</span>
                  <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200 text-xs text-gray-800 italic">
                    {getRemarks(detailsFix) || 'No remarks entered by staff'}
                  </div>
                </div>
              </div>

              {/* Manager Review Notes & Audit Log */}
              {(detailsFix.metadata?.critical_fix_note ||
                detailsFix.metadata?.critical_fix_confirmed_at ||
                detailsFix.metadata?.critical_fix_resolved_at ||
                detailsFix.metadata?.critical_fix_cancelled_at) && (
                <div className="bg-white rounded-xl border border-gray-200 p-3.5 space-y-2">
                  <span className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
                    <FiInfo className="text-indigo-600" />
                    Critical Fix Review Log
                  </span>
                  {detailsFix.metadata?.critical_fix_note && (
                    <div className="space-y-1">
                      <span className="text-gray-500 text-xs">Manager Review Note:</span>
                      <p className="p-2.5 rounded-lg bg-amber-50/70 border border-amber-200 text-xs text-amber-900 font-medium">
                        {detailsFix.metadata.critical_fix_note}
                      </p>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-4 text-xs text-gray-500 pt-1">
                    {detailsFix.metadata?.critical_fix_confirmed_at && (
                      <div>Confirmed at: <strong className="text-gray-700">{formatDateTime(detailsFix.metadata.critical_fix_confirmed_at)}</strong></div>
                    )}
                    {detailsFix.metadata?.critical_fix_resolved_at && (
                      <div>Resolved at: <strong className="text-gray-700">{formatDateTime(detailsFix.metadata.critical_fix_resolved_at)}</strong></div>
                    )}
                    {detailsFix.metadata?.critical_fix_cancelled_at && (
                      <div>Cancelled at: <strong className="text-gray-700">{formatDateTime(detailsFix.metadata.critical_fix_cancelled_at)}</strong></div>
                    )}
                  </div>
                </div>
              )}

              {/* Timeline Section */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden text-xs">
                <div className="bg-gray-50 px-3.5 py-2 font-bold text-gray-700 border-b border-gray-200">
                  Request Lifecycle Timeline
                </div>
                <div className="divide-y divide-gray-100">
                  <div className="px-3.5 py-2 flex items-center justify-between">
                    <span className="text-gray-600">Created At:</span>
                    <span className="font-semibold text-gray-900">{formatDateTime(detailsFix.created_at)}</span>
                  </div>
                  {detailsFix.metadata?.staff_acknowledged_at && (
                    <div className="px-3.5 py-2 flex items-center justify-between">
                      <span className="text-gray-600">Staff Acknowledged:</span>
                      <span className="font-semibold text-gray-900">{formatDateTime(detailsFix.metadata.staff_acknowledged_at)}</span>
                    </div>
                  )}
                  {(detailsFix.completed_at || detailsFix.metadata?.completed_at || detailsFix.metadata?.completion_response_at) && (
                    <div className="px-3.5 py-2 flex items-center justify-between">
                      <span className="text-gray-600">Completed / Responded:</span>
                      <span className="font-semibold text-emerald-700">
                        {formatDateTime(detailsFix.completed_at || detailsFix.metadata?.completed_at || detailsFix.metadata?.completion_response_at)}
                      </span>
                    </div>
                  )}
                  {detailsFix.metadata?.service_rating && (
                    <div className="px-3.5 py-2 flex items-center justify-between bg-amber-50/50">
                      <span className="text-amber-800 font-semibold flex items-center gap-1">
                        <FiStar size={12} className="text-amber-500 fill-amber-400" />
                        Guest Service Rating:
                      </span>
                      <span className="font-bold text-amber-900">⭐ {detailsFix.metadata.service_rating} / 5</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50/80">
              <button
                type="button"
                onClick={() => setDetailsFix(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-100 transition cursor-pointer"
              >
                Close
              </button>

              <div className="flex items-center gap-2">
                {activeSection === 'pending' && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        const target = detailsFix;
                        setDetailsFix(null);
                        setActionNote('');
                        setSelectedFix({ ...target, action: 'confirm' });
                      }}
                      className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs font-semibold flex items-center gap-1 shadow-sm cursor-pointer"
                    >
                      <FiAlertCircle size={13} />
                      <span>Confirm Critical Fix</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const target = detailsFix;
                        setDetailsFix(null);
                        setActionNote('');
                        setSelectedFix({ ...target, action: 'cancel' });
                      }}
                      className="px-3 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-xs font-semibold flex items-center gap-1 cursor-pointer"
                    >
                      <FiXCircle size={13} />
                      <span>Mark Not Critical (No)</span>
                    </button>
                  </>
                )}
                {activeSection === 'confirmed' && (
                  <button
                    type="button"
                    onClick={() => {
                      const target = detailsFix;
                      setDetailsFix(null);
                      setActionNote('');
                      setSelectedFix({ ...target, action: 'resolve' });
                    }}
                    className="px-3.5 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-xs font-semibold flex items-center gap-1.5 shadow-sm cursor-pointer"
                  >
                    <FiCheckCircle size={14} />
                    <span>Mark Resolved</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default CriticalFixes;
